// src/infrastructure/context/UserContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { deleteToken } from "../store/authSlice";
import { getLocalItem, setLocalItem, deleteLocalItem } from "../storage/LocalStorage";
import { userPhotoLL, userInfoLL } from "../storage/LocalStorageIds";
import {
  getProfileImageFromUser,
  normalizeImageSrc,
  sameUser,
} from "../utils/normalizeImageSrc";

const UserContext = createContext();

const parseLSJson = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};

export const UserProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { userId } = useSelector((s) => s.authToken);

  const initialState = useMemo(() => {
    const draft = parseLSJson("userData");               // tu snapshot previo
    const userInfo = getLocalItem(userInfoLL);           // objeto con imagenBase64
    const photo = getLocalItem(userPhotoLL);             // normalmente string (dataURL/base64)

    const isSameStoredUser = draft && userInfo ? sameUser(draft, userInfo) : true;
    const profilePic =
      getProfileImageFromUser(draft) ||
      (isSameStoredUser ? getProfileImageFromUser(userInfo) : null) ||
      (!draft ? normalizeImageSrc(photo) : null);

    if (draft || userInfo || profilePic) {
      return { ...(userInfo || {}), ...(draft || {}), profilePic: profilePic || null };
    }
    return null;
  }, []);

  const [userData, setUserData] = useState(initialState);

  useEffect(() => {
    if (!userId) {
      setUserData(null);
      localStorage.removeItem("userData");
      return;
    }
    if (userData?.idUsuario === userId) return;

    const saved = parseLSJson("userData");
    if (saved?.idUsuario === userId) {
      const normalizedPic =
        getProfileImageFromUser(saved) ||
        (sameUser(saved, getLocalItem(userInfoLL))
          ? getProfileImageFromUser(getLocalItem(userInfoLL))
          : null);
      setUserData({ ...saved, profilePic: normalizedPic || saved.profilePic || null });
      return;
    }

    setUserData(null);
    localStorage.removeItem("userData");
    deleteLocalItem(userPhotoLL);
    deleteLocalItem(userInfoLL);
  }, [userId, userData?.idUsuario]);

  const updateUserData = (patch) => {
    setUserData((prev) => {
      const next = { ...(prev || {}), ...(patch || {}) };
      // si viene foto, persistimos en ambas llaves que usa la app
      if (patch?.profilePic) {
        const norm = normalizeImageSrc(patch.profilePic);
        next.profilePic = norm;
        setLocalItem(userPhotoLL, norm); // tu helper (JSON.stringify por dentro)
        // reflejar también en userInfo.imagenBase64
        const ui = getLocalItem(userInfoLL) || {};
        if (norm?.startsWith?.("data:image")) {
          ui.imagenBase64 = norm;
          setLocalItem(userInfoLL, ui);
        }
      }
      localStorage.setItem("userData", JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    dispatch(deleteToken());
    setUserData(null);
    localStorage.removeItem("userData");
    deleteLocalItem(userPhotoLL);
    deleteLocalItem(userInfoLL);
  };

  return (
    <UserContext.Provider value={{ userData, updateUserData, logout, idUsuario: userData?.idUsuario || userId || null }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser debe ser usado dentro de un UserProvider");
  return ctx;
};
